import ModifyTouch from 'ol/interaction/Modify'
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { StreetGeoJson } from '@containers/map/map.interface'
import { getStreetGeoJsonState } from '@containers/map/map.selectors'
import { getSettingsState } from '@containers/settings/settings.selectors'
import { SetDefaultData } from '../../containers/digitization/digitization.actions'
import { Store, select } from '@ngrx/store'
import Feature from 'ol/Feature'
import Map from 'ol/Map'
import Overlay from 'ol/Overlay'
import View from 'ol/View'
import * as Extent from 'ol/extent'
import GeoJSON from 'ol/format/GeoJSON'
import { Draw, Modify, Select, Snap } from 'ol/interaction'
import {
  Image as ImageLayer,
  Tile as TileLayer,
  Vector as VectorLayer,
} from 'ol/layer'
import { Projection, get, getPointResolution, transform } from 'ol/proj'
import { ImageWMS, TileWMS, Vector as VectorSource } from 'ol/source'
import {
  Circle as CircleStyle,
  Fill,
  RegularShape,
  Stroke,
  Style,
} from 'ol/style'
import { Subscription } from 'rxjs'
import { environment } from '@env/environment'
import { Geometry, MultiPolygon, Polygon } from 'ol/geom'
// import { environment } from '../../../environments/environment';
import { bbox } from 'ol/loadingstrategy.js'
import FillPattern from 'ol-ext/style/FillPattern.js'
import DrawHole from 'ol-ext/interaction/DrawHole'
import Toggle from 'ol-ext/control/Toggle'
import Button from 'ol-ext/control/Button'
import Bar from 'ol-ext/control/Bar'
import { getUpdateBuildingState } from '@containers/digitization/digitization.selectors'
import { getActiveLayers } from '@shared/shared.select'
import LayerPopup from 'ol-ext/control/LayerPopup'
import { FetchActiveLayersRequest } from '@shared/shared.actions'
import Control from 'ol/control/Control'
import { addUniqueCoordinates } from '@shared/shared.utils'

@Component({
  selector: 'digitization-map',
  templateUrl: './digitization-map.component.html',
  styleUrls: ['./digitization-map.component.css'],
})
export class DigitizationMapComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  public map: Map
  public draw: Draw
  public _polygonFeature: Draw
  public vector: VectorLayer<any>
  public streetsLayer: VectorLayer<any>
  public snap: Snap
  public overlay: Overlay
  public isPopupVisible: boolean
  public raster: ImageLayer<any>
  public pointFeature: Feature
  public modify: Modify
  public position: number[] = []
  public settings: any
  private settings$: Subscription
  public popupData: any = {}
  public popupDataKeys = []
  public extent: any
  public resolution: any
  public isEditOpen = false
  public isCreateOpen = false
  public selectedFeature: Feature
  public drawHole: DrawHole
  public featureLoaded = false
  public selectCtrl: Toggle
  public editor: Bar
  public mainbar: Bar
  public selectInteraction: Select
  public removeCtrl: Button
  public geojson: string
  public editable: boolean = false
  public data: any = {}
  private data$: Subscription
  private interval: any
  private layerPopup: LayerPopup
  private activeLayersState$: Subscription
  public activeLayers = []
  public layerFeatures: any[] = []
  public isCoordinatesPopupVisible = false
  public highlightedPropertyCode: string | null = null

  @ViewChild('olPopup', { static: false }) popup: ElementRef

  constructor(public store: Store<any>) {}

  ngOnInit() {
    this.data$ = this.store
      .pipe(select(getUpdateBuildingState))
      .subscribe((state) => {
        const data = state.get('data').toJS()

        if (
          JSON.stringify(this.data.buildingLayerFeatures) !==
          JSON.stringify(data.buildingLayerFeatures)
        ) {
          this.layerFeatures = data.buildingLayerFeatures ?? []
          this.markSelectedLayers.bind(this)()
        }

        if (this.isCreateOpen && data && data.id && this.selectedFeature) {
          this.selectedFeature.setId(data.id)
        }

        if (
          data &&
          JSON.stringify(this.data) !== JSON.stringify(data) &&
          this.vector &&
          this.data.id
        ) {
          this.data = data

          const targetFeature = this.vector.getSource().getFeatureById(data.id)

          Object.keys(data).forEach(function (propertyKey) {
            if (data[propertyKey] && data[propertyKey].name) {
              targetFeature.set(propertyKey, data[propertyKey].name)
            } else {
              targetFeature.set(propertyKey, data[propertyKey])
            }
          })

          targetFeature.changed()

          this.vector.getSource().changed()
        }
      })

    this.activeLayersState$ = this.store
      .pipe(select(getActiveLayers))
      .subscribe((state) => {
        if (this.activeLayers.length === 0) {
          this.activeLayers = state.toJS()
          this.interval = setInterval(() => {
            if (this.map) {
              this.addLayersToMap()

              clearInterval(this.interval)
            }
          })
        }
      })

    this.store.dispatch<any>(new FetchActiveLayersRequest())
  }

  addCoordinatesFinderButton(): void {
    const button = document.createElement('button')
    const icon = document.createElement('img')
    icon.setAttribute('src', '/assets/search-icon.svg')
    button.appendChild(icon)
    button.addEventListener('click', () => {
      this.isCoordinatesPopupVisible = !this.isCoordinatesPopupVisible
    })
    button.className = 'coordinates-button'

    const controlDiv = document.createElement('div')
    controlDiv.className = 'coordinates-button-control'
    controlDiv.appendChild(button)

    const controlElement = document.createElement('div')
    controlElement.className = 'ol-unselectable ol-control coordinates-control'
    controlElement.appendChild(controlDiv)

    const mapControls = this.map.getControls()
    mapControls.push(
      new (class extends Control {
        constructor() {
          super({
            element: controlElement,
          })
        }
      })(),
    )
  }

  highlightFeatureByCode(item: any): void {
    this.highlightedPropertyCode = item?.propertyCode || null
    if (this.vector) {
      this.vector.changed()
    }
  }

  setOpacity = (hex: string, alpha: number) =>
    hex
      ? `${hex}${Math.floor(alpha * 255)
          .toString(16)
          .padStart(2)}`
      : null

  addLayersToMap() {
    const _this = this

    if (!!this.layerPopup) return

    if (!this.layerPopup && this.activeLayers.length) {
      this.activeLayers.forEach(({ id, color, name }) => {
        const source = new VectorSource({
          format: new GeoJSON(),
          url: `${environment.API_ENDPOINT}/gis/public/layer/${id}`,
        })

        const styles = {
          MultiPolygon: new Style({
            stroke: new Stroke({
              color: _this.setOpacity(color, 0.5),
              width: 1,
            }),
            fill: new Fill({
              color: _this.setOpacity(color, 0.3),
            }),
          }),
          Polygon: new Style({
            stroke: new Stroke({
              color: _this.setOpacity(color, 0.5),
              width: 1,
            }),
            fill: new Fill({
              color: _this.setOpacity(color, 0.3),
            }),
          }),
        }

        const vector = new VectorLayer({
          properties: {
            layerId: id,
            name,
          },
          visible: false,
          source,
          style: function (feature: any) {
            return styles[feature.getGeometry().getType()]
          },
        })

        _this.map.addLayer(vector)
      })

      this.layerPopup = new LayerPopup()

      _this.map.addControl(this.layerPopup)

      this.layerPopup.on('layer:visible', this.markSelectedLayers.bind(this))
    }
  }

  formChange(data: any) {
    this.layerFeatures = data

    this.markSelectedLayers()
  }

  onCreateBuilding() {
    this.closeEdit()
    this.store.dispatch<any>(new SetDefaultData('updateBuilding'))

    this.isCreateOpen = true
    this.isEditOpen = true
    this.editable = true

    this.onEditableChange(this.editable)
  }

  clearSelectedLayerFeature() {
    if (this.map) {
      this.map.getLayers().forEach((mapLayer) => {
        const properties = mapLayer.getPropertiesInternal()
        const mapLayerId = properties?.layerId
        const source = properties.source as VectorSource
        const mark = () => {
          if (source.getFeatures) {
            source.getFeatures().forEach((feature) => {
              const props = feature.getPropertiesInternal()
              const activeLayer = this.activeLayers.find(
                ({ id }) => id === mapLayerId,
              )

              if (props.highlighted) {
                feature.setProperties({
                  ...feature.getPropertiesInternal(),
                  highlighted: false,
                })
                feature.setStyle(
                  new Style({
                    stroke: new Stroke({
                      color: this.setOpacity(activeLayer.color, 0.5),
                      width: 1,
                    }),
                    fill: new Fill({
                      color: this.setOpacity(activeLayer.color, 0.3),
                    }),
                  }),
                )
              }
            })
          }
        }

        if (mapLayer.getVisible() && source.getFeatures) {
          if (source.getFeatures().length) {
            mark()
          } else {
            source.on('featuresloadend', mark)
          }
        }
      })
    }
  }
  markSelectedLayers() {
    this.clearSelectedLayerFeature.bind(this)()

    if (this.layerFeatures.length) {
      this.layerFeatures.forEach(({ layerFeature, layerId, id: featureId }) => {
        this.map.getLayers().forEach((mapLayer) => {
          const properties = mapLayer.getPropertiesInternal()
          const mapLayerId = properties?.layerId
          const source = properties.source as VectorSource
          const mark = () => {
            source.getFeatures().forEach((feature) => {
              const props = feature.getPropertiesInternal()
              const activeLayer = this.activeLayers.find(
                ({ id }) => id === mapLayerId,
              )

              if (props.highlighted) {
                feature.setProperties({
                  ...feature.getPropertiesInternal(),
                  highlighted: false,
                })
                feature.setStyle(
                  new Style({
                    stroke: new Stroke({
                      color: this.setOpacity(activeLayer.color, 0.5),
                      width: 1,
                    }),
                    fill: new Fill({
                      color: this.setOpacity(activeLayer.color, 0.3),
                    }),
                  }),
                )
              }

              const fid = layerFeature?.id ?? featureId

              if (fid == props.id) {
                feature.setProperties({
                  ...feature.getPropertiesInternal(),
                  highlighted: true,
                })
                feature.setStyle(
                  new Style({
                    stroke: new Stroke({
                      color: this.setOpacity(activeLayer.color, 0.5),
                      width: 1,
                    }),
                    fill: new FillPattern({
                      pattern: 'hatch',
                      color: activeLayer.color,
                      scale: 1,
                      size: 1,
                      spacing: 3,
                      angle: 45,
                      fill: new Fill({
                        color: this.setOpacity(activeLayer.color, 0.5),
                      }),
                    }),
                  }),
                )
              }
            })

            source.un('featuresloadend', mark)
          }

          if (mapLayer.getVisible()) {
            const lid = layerFeature?.layerId ?? layerId
            if (mapLayerId === lid) {
              if (source.getFeatures().length) {
                mark()
              } else {
                source.on('featuresloadend', mark)
              }
            }
          }
        })
      })
    }
  }

  ngAfterViewInit() {
    this.settings$ = this.store
      .pipe(select(getSettingsState))
      .subscribe((state) => {
        this.settings = state.get('data')
        if (this.settings.size && !this.map) {
          this.initMap()
        }
      })
  }

  ngOnDestroy() {
    this.data$.unsubscribe()
    this.settings$.unsubscribe()

    this.unsubscribeEditableControls()

    if (this.activeLayersState$) {
      this.activeLayersState$.unsubscribe()
    }

    if (this.layerPopup) {
      this.layerPopup.un('layer:visible', this.markSelectedLayers.bind(this))
    }
  }

  unsubscribeEditableControls() {
    if (this.draw) {
      this.draw.un('drawend', this.onDrawEnd.bind(this))
    }

    if (this.modify) {
      this.modify.un('modifyend', this.onDrawEnd.bind(this))
    }

    if (this.drawHole) {
      this.drawHole.un('drawend', this.onDrawEnd.bind(this))
      this.drawHole.un('modifyend', this.onDrawEnd.bind(this))
    }

    if (this.selectInteraction) {
      this.selectInteraction.un('select', this.onSelect.bind(this))
    }
  }

  initMap() {
    const _this = this
    const {
      GIS_GEOSERVER_URL,
      GIS_GEOSERVER_WORKSPACE,
      GIS_BASE_LAYER,
      GIS_MAP_EXTENT,
    } = this.settings.toJS().gis
    this.overlay = new Overlay({
      element: this.popup.nativeElement,
      offset: [-128, 20],
      autoPan: false,
    })

    const extent = GIS_MAP_EXTENT.split(',').map((c: string) => parseFloat(c))
    const origin = extent[0] + ',' + extent[1]

    this.raster = new ImageLayer({
      source: new ImageWMS({
        url: `${GIS_GEOSERVER_URL}/${GIS_GEOSERVER_WORKSPACE}/wms`,
        params: {
          FORMAT: 'image/jpeg',
          VERSION: '1.1.1',
          tiled: true,
          LAYERS: `${GIS_GEOSERVER_WORKSPACE}:${GIS_BASE_LAYER}`,
          exceptions: 'application/vnd.ogc.se_inimage',
          tilesOrigin: origin,
        },
      }),
    })

    const source = new VectorSource({
      format: new GeoJSON(),
      loader: function (extent, resolution, projection, success, failure) {
        const proj = projection.getCode()
        const url = `${
          environment.API_ENDPOINT
        }/gis/public/buildings?bbox=${extent.join(
          ',',
        )},${proj}&maxFeatures=1000`
        const xhr = new XMLHttpRequest()
        xhr.open('GET', url)
        const onError = function () {
          source.removeLoadedExtent(extent)
          failure()
        }
        xhr.onerror = onError
        xhr.onload = function () {
          if (xhr.status == 200 && xhr.responseText) {
            const features = (
              source
                .getFormat()
                .readFeatures(xhr.responseText) as Feature<Geometry>[]
            ).map((feature) => {
              feature.setProperties({
                ...feature.getPropertiesInternal(),
                selectable: true,
              })

              return feature
            })

            const selectedId = _this.selectedFeature
              ? _this.selectedFeature.getId()
              : null

            source.getFeatures().forEach((f) => {
              if (f.getId() !== selectedId) {
                source.removeFeature(f)
              }
            })

            source.addFeatures(features.filter((f) => f.getId() !== selectedId))

            success(features)
          } else {
            onError()
          }
        }
        xhr.send()
      },
      strategy: function (extent, resolution) {
        if (
          this.resolution != resolution ||
          JSON.stringify(extent) !== JSON.stringify(this.extent)
        ) {
          this.loadedExtentsRtree_.clear()
          this.extent = extent
          this.resolution = resolution
        }
        return [extent]
      },
    })

    const styles = {
      MultiPolygon: new Style({
        stroke: new Stroke({
          color: '#3273dc',
          width: 1,
        }),
        fill: new FillPattern({
          pattern: 'hatch',
          color: '#3273dc',
          scale: 1,
          size: 1,
          spacing: 3,
          angle: 45,
          // fill: new Fill({ color: 'transparent' }),
          fill: new Fill({ color: 'rgba(50,115,220,0.1)' }),
        }),
      }),
      Polygon: new Style({
        stroke: new Stroke({
          color: '#3273dc',
          width: 1,
        }),
        fill: new FillPattern({
          pattern: 'hatch',
          color: '#3273dc',
          scale: 1,
          size: 1,
          spacing: 3,
          angle: 45,
          // fill: new Fill({ color: 'transparent' }),
          fill: new Fill({ color: 'rgba(50,115,220,0.1)' }),
        }),
      }),
      Point: new Style({
        image: new CircleStyle({
          radius: 5,
          fill: new Fill({ color: 'rgba(255,0,0,0.5)' }),
          stroke: new Stroke({ color: '#fff', width: 0.5 }),
        }),
      }),
    }

    const highlightStyles = {
      MultiPolygon: new Style({
        stroke: new Stroke({
          color: '#d9534f',
          width: 3,
        }),
        fill: new Fill({
          color: 'rgba(217, 83, 79, 0.4)',
        }),
      }),
      Polygon: new Style({
        stroke: new Stroke({
          color: '#d9534f',
          width: 3,
        }),
        fill: new Fill({
          color: 'rgba(217, 83, 79, 0.4)',
        }),
      }),
      Point: new Style({
        image: new CircleStyle({
          radius: 8,
          fill: new Fill({ color: 'rgba(255, 0, 0, 0.8)' }),
          stroke: new Stroke({ color: '#fff', width: 2 }),
        }),
      }),
    }

    this.vector = new VectorLayer({
      source,
      style: (feature: any) => {
        const geomType = feature.getGeometry().getType()
        if (this.highlightedPropertyCode && feature.get('prop_code') === this.highlightedPropertyCode) {
          return highlightStyles[geomType]
        }
        return styles[geomType]
      },
      zIndex: 100,
    })

    this.map = new Map({
      layers: [this.raster, this.vector],
      target: 'ol-digitization',
      overlays: [this.overlay],
      view: new View({
        projection: 'EPSG:4326',
        center: this.position.length
          ? this.position
          : [(extent[0] + extent[2]) / 2, (extent[1] + extent[3]) / 2],
        zoom: 15,
      }),
    })

    this.addControls()

    this.map.on('pointermove', ({ pixel }) => {
      const feature = this.map.forEachFeatureAtPixel(pixel, function (feature) {
        return feature
      }) as any

      if (
        feature &&
        feature.getId() &&
        ((this.selectedFeature &&
          feature.getId() !== this.selectedFeature.getId()) ||
          !this.selectedFeature)
      ) {
        const geometry = feature.getGeometry() as any
        const coordinates = geometry.getCoordinates()
        let newCoordinates: any[]

        const {
          id,
          street,
          street_number,
          name,
          community,
          zone,
          stories,
          prop_id,
          prop_code,
        } = feature.values_
        this.popupData = {
          id,
          street,
          street_number,
          name,
          community,
          zone,
          stories,
          prop_id,
          prop_code,
          type: geometry.getType(),
        }
        this.popupDataKeys = Object.keys(this.popupData)
        this.isPopupVisible = true

        if (coordinates.length) {
          var centroid = Extent.getCenter(geometry.getExtent())
          newCoordinates = centroid
        } else {
          newCoordinates = coordinates
        }

        this.overlay.setPosition(newCoordinates)
      } else {
        this.isPopupVisible = false
      }
    })
  }

  openEdit(feature: Feature) {
    this.selectedFeature = feature
    this.isPopupVisible = false
    this.isEditOpen = true
  }

  closeEdit() {
    if (this.selectedFeature) {
      const id = this.selectedFeature.getId()

      if (!id) {
        this.vector.getSource().removeFeature(this.selectedFeature)
      }
    }

    this.selectInteraction.getFeatures().remove(this.selectedFeature)
    this.selectInteraction.setActive(true)

    this.removeEditableControls()
    this.removeCtrl.getButtonElement().setAttribute('disabled', 'true')

    this.isEditOpen = false
    this.isCreateOpen = false
    this.selectedFeature = null
  }

  onEditableChange(value: boolean) {
    this.editable = value

    this.selectCtrl.setActive(true)

    if (value) {
      this.addEditableControls()
      this.removeCtrl.getButtonElement().removeAttribute('disabled')
    } else {
      this.removeEditableControls()
      this.removeCtrl.getButtonElement().setAttribute('disabled', 'true')
    }
  }

  addEditableControls() {
    this.modify = new ModifyTouch({
      source: this.vector.getSource(),
      features: (this.selectCtrl.getInteraction() as Select).getFeatures(),
    })

    this.map.addInteraction(this.modify)
    this.modify.on('modifyend', this.onDrawEnd.bind(this))

    this.editor.getControls().forEach((c: any) => {
      if (c.getInteraction && c.getInteraction() instanceof Draw) {
        c.setDisable(false)
      }
    })
  }

  removeEditableControls() {
    this.map.removeInteraction(this.modify)

    this.editor.getControls().forEach((c: any) => {
      if (c.getInteraction && c.getInteraction() instanceof Draw) {
        c.setDisable(true)
      }
    })
  }

  addControls() {
    const _this = this
    this.mainbar = new Bar({
      toggleOne: true,
      group: true,
      className: 'ol-controls',
    })

    this.map.addControl(this.mainbar)

    this.editor = new Bar({
      toggleOne: true,
      group: false,
    })
    this.mainbar.addControl(this.editor)

    const selectBar = new Bar()

    this.selectCtrl = new Toggle({
      html: '<i class="fa-solid fa-arrow-pointer"></i>',
      title: 'Select',
      interaction: new Select({
        hitTolerance: 2,
        condition: function (event) {
          if (event.type === 'singleclick') {
            // if (_this.editable) {
            const selectable = _this.map.forEachFeatureAtPixel(
              event.pixel,
              function (e: Feature) {
                return e.getProperties().selectable
              },
            )

            return selectable
            // }
          }
          return false
        },
      }),
      bar: selectBar,
      autoActive: true,
      active: true,
    })

    this.editor.addControl(this.selectCtrl)

    this.removeCtrl = new Button({
      html: '<i class="fa fa-times"></i>',
      handleClick: function () {
        if (_this.selectedFeature) {
          _this.selectInteraction.getFeatures().remove(_this.selectedFeature)
          _this.vector.getSource().removeFeature(_this.selectedFeature)
          _this.vector.getSource().changed()
          _this.selectedFeature.setGeometry(new MultiPolygon([]))
        }
      },
    })

    this.editor.addControl(this.removeCtrl)

    this.removeCtrl.getButtonElement().setAttribute('disabled', 'true')

    const polygon = new Toggle({
      html: '<i class="fa-solid fa-draw-polygon"></i>',
      disable: true,
      interaction: new Draw({
        type: 'MultiPolygon',
        source: _this.vector.getSource(),
      }),
    })

    this.editor.addControl(polygon)

    const drawhole = new Toggle({
      disable: true,
      html: '<i class="fa-solid fa-scissors"></i>',
      interaction: new DrawHole({
        type: 'MultiPolygon',
        layers: [this.vector],
      }),
    })

    this.editor.addControl(drawhole)

    this.draw = polygon.getInteraction() as Draw
    this.drawHole = drawhole.getInteraction() as DrawHole
    this.selectInteraction = this.selectCtrl.getInteraction() as Select

    this.draw.on('drawend', this.onDrawEnd.bind(this))
    this.drawHole.on('drawend', this.onDrawHoleEnd.bind(this))
    this.selectInteraction.on('select', this.onSelect.bind(this))

    this.addCoordinatesFinderButton()
  }

  onSelect({ selected }) {
    if (selected.length) {
      const feature = selected[0]

      this.openEdit(feature)
    } else {
      this.closeEdit()
    }
  }

  onDrawHoleEnd({ feature, type, features }) {
    if (type === 'drawend') {
      this.geojson = new GeoJSON().writeGeometry(feature.getGeometry())
    }
  }

  onDrawEnd({ feature, type, features }) {
    let json = null

    if (!this.selectedFeature) {
      feature.setProperties({
        ...feature.getPropertiesInternal(),
        selectable: true,
      })
      this.selectedFeature = feature
    }

    if (type === 'drawend') {
      // if (feature && this.selectedFeature) {
      //   const geometry = feature.getGeometry()
      //   const newCoordinates = geometry.getCoordinates()
      //   const existingFeature = this.selectedFeature.clone()
      //   const existingGeometry =
      //     existingFeature.getGeometry() instanceof MultiPolygon
      //       ? (existingFeature.getGeometry() as MultiPolygon)
      //       : new MultiPolygon([])
      //   const existingCoordinates = existingGeometry.getCoordinates()

      //   existingCoordinates.push.apply(existingCoordinates, newCoordinates)
      //   existingGeometry.setCoordinates(existingCoordinates)

      //   feature.setGeometry(existingGeometry)
      //   feature.setId(this.selectedFeature.getId())
      //   feature.changed()

      //   this.vector.getSource().removeFeature(this.selectedFeature)
      //   this.vector.getSource().changed()

      //   this.selectInteraction.getFeatures().clear()
      //   this.selectInteraction.getFeatures().push(feature)
      //   this.selectInteraction.changed()

      //   this.selectedFeature = feature

      //   json = new GeoJSON().writeGeometry(feature.getGeometry())
      // }
      if (feature && this.selectedFeature) {
        const geometry = feature.getGeometry()
        const newCoordinates = geometry.getCoordinates()
        const existingFeature = this.selectedFeature.clone()
        const existingGeometry =
          existingFeature.getGeometry() instanceof MultiPolygon
            ? (existingFeature.getGeometry() as MultiPolygon)
            : new MultiPolygon([])
        const existingCoordinates = existingGeometry.getCoordinates()

        // Only add coordinates if they are different from existing ones
        addUniqueCoordinates(existingCoordinates, newCoordinates)

        existingGeometry.setCoordinates(existingCoordinates)

        feature.setGeometry(existingGeometry)
        feature.setId(this.selectedFeature.getId())
        feature.set('selectable', true)
        feature.changed()

        this.vector.getSource().removeFeature(this.selectedFeature)
        this.vector.getSource().changed()

        this.selectInteraction.getFeatures().clear()
        this.selectInteraction.getFeatures().push(feature)
        this.selectInteraction.changed()

        this.selectedFeature = feature

        json = new GeoJSON().writeGeometry(feature.getGeometry())
      }
    }

    if (type === 'modifyend') {
      const selectedFeature = features.pop() as Feature
      json = new GeoJSON().writeGeometry(selectedFeature.getGeometry())

      if (selectedFeature) {
        this.selectedFeature.setGeometry(selectedFeature.getGeometry())
      }
    }

    this.geojson = json
  }
}
